import type * as CSS from 'csstype';
import React, { forwardRef, type ReactElement, type ReactNode, type Ref, useState } from 'react';
import styled, { type FlattenSimpleInterpolation } from 'styled-components';

import type { IconProps } from '#@/components/Icon';

import { PrefixLabel, SuffixLabel } from '../InputLabel';
import { IconWrapper } from './IconWrapper';
import {
  type AppearanceStyleProps,
  getDefaultAppearanceStyle,
  type InputContentPosition,
  type InputSize,
  Layout,
  StyledIcon,
  StyledIconClickable,
} from './Styles';

export type InputRenderProps = Omit<React.ComponentPropsWithRef<'input'>, 'onChange' | 'ref'> & {
  onChange: (value: string, event: React.ChangeEvent<HTMLInputElement>) => void;
  ref?: Ref<HTMLInputElement>;
  borderless?: boolean;
};

export type RenderInput = (props: InputRenderProps) => ReactNode;

export interface InputProps
  extends Omit<React.ComponentPropsWithoutRef<'input'>, 'onChange' | 'size'> {
  icon?: IconProps['type'];
  iconStyle?: IconProps['style'];
  iconPosition?: InputContentPosition;
  iconWidth?: number;
  renderIconTooltip?: (icon: ReactElement) => ReactElement;
  onIconClick?: () => void;
  prefixLabel?: React.ReactNode;
  suffixLabel?: React.ReactNode;
  invalid?: boolean;
  filter?: RegExp;
  onChange?: (value: string, event: React.ChangeEvent<HTMLInputElement>) => void;
  renderInput?: RenderInput;
  textAlign?: CSS.Property.TextAlign;
  cursor?: CSS.Property.Cursor;
  'data-testid'?: string;
  prefixLabelRightOffset?: number;
  borderless?: boolean;
  size?: InputSize;
  iconTooltip?: string;
  /**
   * Allows specifying custom appearance for the input.
   *
   * `Input` will always provide CSS required for its layout and other
   * fundamental features. Otherwise, visuals are injected via this prop. If no
   * value is provided, a standard style is used. However, if a custom style is
   * passed in (in the form of a `css` tag template), then it will _replace_ the
   * default.
   *
   * If you just want to change one or two things about the default appearance,
   * then it makes more sense to use `styled(Input)`. Use this prop if you need
   * an entirely different looking input, while preserving all the features of
   * the standard component.
   */
  getAppearanceStyle?: (props: AppearanceStyleProps) => FlattenSimpleInterpolation;
}

// eslint-disable-next-line react/display-name
const InputBase = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      icon,
      onIconClick,
      iconStyle,
      iconTooltip,
      iconWidth,
      prefixLabel,
      suffixLabel,
      className,
      invalid = false,
      filter,
      onChange = () => {},
      name,
      readOnly,
      renderInput = (renderProps) => (
        <input
          {...renderProps}
          onChange={(event) => renderProps.onChange(event.target.value, event)}
        />
      ),
      textAlign,
      cursor,
      iconPosition,
      prefixLabelRightOffset = 0,
      borderless = false,
      size,
      disabled,
      getAppearanceStyle = getDefaultAppearanceStyle,
      renderIconTooltip,
      ...inputProps
    },
    ref,
  ) => {
    const [prefixLabelRef, setPrefixLabelRef] = useState<HTMLDivElement | null>(null);
    const [suffixLabelRef, setSuffixLabelRef] = useState<HTMLDivElement | null>(null);

    const handleChange: InputRenderProps['onChange'] = (value, event) => {
      if (!filter || filter.test(value)) {
        onChange(value, event);
      }
    };

    const isIconClickable = Boolean(onIconClick || iconTooltip !== undefined);

    return (
      <Layout
        className={className}
        data-cy-input-invalid={invalid}
        icon={icon}
        prefixLabel={prefixLabel}
        prefixLabelWidth={prefixLabelRef?.getBoundingClientRect().width}
        prefixLabelRightOffset={prefixLabelRightOffset}
        suffixLabel={suffixLabel}
        suffixLabelWidth={suffixLabelRef?.getBoundingClientRect().width}
        iconPosition={iconPosition}
        $appearanceStyle={getAppearanceStyle({
          invalid,
          textAlign,
          cursor,
          size,
          borderless,
          disabled,
        })}
      >
        {icon &&
          (renderIconTooltip ? (
            renderIconTooltip(<StyledIcon style={iconStyle} type={icon} size={18} />)
          ) : (
            <IconWrapper
              isTriggerElementClickable={Boolean(onIconClick)}
              tooltipText={iconTooltip}
              clickable={Boolean(onIconClick)}
              $position={iconPosition}
              $iconWidth={iconWidth}
            >
              {isIconClickable ? (
                <StyledIconClickable
                  style={iconStyle}
                  onClick={onIconClick}
                  type={icon}
                  size={18}
                />
              ) : (
                <StyledIcon style={iconStyle} type={icon} size={18} $position={iconPosition} />
              )}
            </IconWrapper>
          ))}
        {!icon && prefixLabel && (
          <PrefixLabel ref={setPrefixLabelRef} data-prefix-label>
            {prefixLabel}
          </PrefixLabel>
        )}
        {renderInput({
          ...inputProps,
          disabled,
          readOnly,
          name,
          onChange: handleChange,
          ref,
        })}
        {suffixLabel && (
          <SuffixLabel ref={setSuffixLabelRef} data-suffix-label>
            {suffixLabel}
          </SuffixLabel>
        )}
      </Layout>
    );
  },
);

export const Input = styled(InputBase)``;

Input.displayName = 'Input';
